import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from 'react-router-dom'

import '@layerfi/components/index.css'
import './App.scss'
import './themes/AppTheme.scss'

import { LoggedOutRedirect } from './components/LoggedOutRedirect'
import { useAuth } from './contexts/AuthContext'
import { DemoViewerSettingsContext, useDemoViewerSettingsProvider } from './contexts/DemoViewerSettingsContext'
import { SidebarContext, useSidebar } from './contexts/SidebarContext'
import { BankTransactions } from './pages/accounting/bank-transactions'
import { Ledger } from './pages/accounting/ledger'
import { LinkAccountsPage } from './pages/accounting/link-accounts/LinkAccountsPage'
import { MileageTracking } from './pages/accounting/mileage-tracking'
import { Reports as OldReports } from './pages/accounting/reports/Reports'
import { TaxEstimates } from './pages/accounting/tax-estimates'
import { TimeTracking } from './pages/accounting/time-tracking'
import { UnifiedReports } from './pages/accounting/unified-reports'
import { Admin } from './pages/Admin'
import { BusinessId } from './pages/BusinessId'
import { DemoSettings } from './pages/DemoSettings'
import { Environment } from './pages/Environment'
import { Impersonate } from './pages/Impersonate'
import { InvoicesPage } from './pages/Invoices'
import { MyLandingPage } from './pages/landing-page'
import { LandingPage } from './pages/LandingPage'
import { Login } from './pages/Login'
import { SolopreneurPage } from './pages/solopreneur/solopreneur'
import { Root } from './Root'

const HomeRedirect = () => {
  const { clientName, isLoggedIn, isAdmin } = useAuth()

  if (!isLoggedIn) {
    return <Navigate to='login' replace />
  }

  if (isAdmin) {
    return <Navigate to='admin' replace />
  }

  return <Navigate to={`/${clientName}`} replace />
}

const router = createBrowserRouter([
  {
    path: '',
    element: <HomeRedirect />,
  },
  {
    path: 'admin',
    element: <Admin />,
  },
  {
    path: 'login',
    element: <Login />,
  },
  {
    path: 'impersonate',
    element: <Impersonate />,
    children: [
      {
        path: ':environment',
        element: <Environment />,
        children: [
          {
            path: ':businessId',
            element: <BusinessId />,
            children: [
              {
                path: '',
                element: <Navigate to='overview' replace />,
              },
              {
                path: 'overview',
                element: <LandingPage />,
              },
              {
                path: 'bank-transactions',
                element: <BankTransactions />,
              },
              {
                path: 'reports',
                element: <UnifiedReports />,
              },
              {
                path: 'old-reports',
                element: <OldReports />,
              },
              {
                path: 'ledger',
                element: <Ledger />,
              },
              {
                path: 'tax-estimates',
                element: <TaxEstimates />,
              },
              {
                path: 'invoices',
                element: <InvoicesPage />,
              },
              {
                path: 'mileage-tracking',
                element: <MileageTracking />,
              },
              {
                path: 'time-tracking',
                element: <TimeTracking />,
              },
              {
                path: 'landing-page',
                element: <MyLandingPage />,
              },
              {
                path: 'solopreneur',
                element: <SolopreneurPage />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: ':demoName',
    element: <Root />,
    children: [
      {
        path: '',
        element: <Navigate to='overview' replace />,
      },
      {
        path: 'link-accounts',
        element: <LinkAccountsPage />,
      },
      {
        path: 'overview',
        element: <LandingPage />,
      },
      {
        path: 'bank-transactions',
        element: <BankTransactions />,
      },
      {
        path: 'reports',
        element: <UnifiedReports />,
      },
      {
        path: 'old-reports',
        element: <OldReports />,
      },
      {
        path: 'ledger',
        element: <Ledger />,
      },
      {
        path: 'tax-estimates',
        element: <TaxEstimates />,
      },
      {
        path: 'invoices',
        element: <InvoicesPage />,
      },
      {
        path: 'mileage-tracking',
        element: <MileageTracking />,
      },
      {
        path: 'time-tracking',
        element: <TimeTracking />,
      },
      {
        path: 'landing-page',
        element: <MyLandingPage />,
      },
      {
        path: 'solopreneur',
        element: <SolopreneurPage />,
      },
      {
        path: 'demo-settings',
        element: <DemoSettings />,
      },
    ],
  },
  {
    path: '*',
    element: <LoggedOutRedirect />,
  },
])

function App() {
  const sidebarData = useSidebar()
  const settingsData = useDemoViewerSettingsProvider()

  return (
    <div className='root-container'>
      <DemoViewerSettingsContext.Provider value={settingsData}>
        <SidebarContext.Provider value={sidebarData}>
          <RouterProvider router={router} />
        </SidebarContext.Provider>
      </DemoViewerSettingsContext.Provider>
    </div>
  )
}

export default App
